import { useEffect, useMemo, useState, type FormEventHandler, type KeyboardEventHandler, type MouseEventHandler } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, Plus, Trash2 } from "lucide-react";

import {
  api,
  type MembershipAssignment,
  type Role,
  type Tenant,
  type UserSummary,
} from "@/lib/api";
import { useAuth } from "@/auth/auth-provider";
import { hasCapability } from "@/auth/capabilities";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelector } from "@/components/searchable-selector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/toast";
import { PageHeader } from "@/components/page-header";
import { RolePill } from "@/components/role-pill";
import { initialsFromName } from "@/lib/format";
import { ListPagination, ListSearch, ListSort, useBrowsingState, usePagedList } from "@/components/list-controls";
import { ListFilter } from "@/components/list-filter";
import { AdminFilterGroup, AdminFilterPanel, AdminListLayout } from "@/components/admin-filter-panel";
import { useDialogDraft } from "@/components/dialog-draft";

const ALL_ROLES: Role[] = ["platform_admin", "tenant_admin", "tenant_viewer"];

const createUserSchema = z.object({
  email: z.string().min(3, "Required").email("Enter a valid email"),
  display_name: z.string().min(1, "Required").max(120),
  password: z.string().min(8, "At least 8 characters"),
});
type CreateValues = z.infer<typeof createUserSchema>;
type MembershipDraft = MembershipAssignment & { local_id: number };

export function UsersPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { me } = useAuth();

  const isPlatformAdmin = hasCapability(me, "catalog:write");

  const tenantsQuery = useQuery({
    queryKey: ["tenants"],
    queryFn: () => api.tenants(),
    enabled: isPlatformAdmin,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [deleting, setDeleting] = useState<UserSummary | null>(null);
  const [managing, setManaging] = useState<UserSummary | null>(null);
  const [createFeedback, setCreateFeedback] = useState<string | null>(null);
  const createDraft = useDialogDraft();
  const membershipDraft = useDialogDraft();
  const resetCreateDraft = createDraft.reset;
  const resetMembershipDraft = membershipDraft.reset;
  useEffect(() => {
    if (!createOpen) resetCreateDraft();
  }, [createOpen, resetCreateDraft]);
  useEffect(() => {
    if (managing === null) resetMembershipDraft();
  }, [managing, resetMembershipDraft]);
  const [organizationFilter, setOrganizationFilter] = useBrowsingState("users:organization", "");
  const [roleFilter, setRoleFilter] = useBrowsingState("users:role", "");
  const { list, query: usersQuery } = usePagedList("users", {
    organization_id: organizationFilter, role: roleFilter,
  }, [{ key: "name", label: "Name" }, { key: "email", label: "Email" }], api.browseUsers);

  const tenants = useMemo<Tenant[]>(() => tenantsQuery.data ?? [], [tenantsQuery.data]);
  const tenantOptions = useMemo<Tenant[]>(() => {
    if (isPlatformAdmin) return tenants;
    if (me?.tenant_id === null || me?.tenant_id === undefined) return [];
    const existing = tenants.find((tenant) => tenant.tenant_id === me.tenant_id);
    return [
      existing ?? {
        tenant_id: me.tenant_id,
        name: "Current organization",
        external_key: String(me.tenant_id),
        kind: "vendor",
      },
    ];
  }, [isPlatformAdmin, me?.tenant_id, tenants]);
  const roleOptions = isPlatformAdmin
    ? ALL_ROLES
    : (["tenant_admin", "tenant_viewer"] satisfies Role[]);
  const organizationOptions = tenantOptions.map((tenant) => ({
    value: String(tenant.tenant_id), label: tenant.name,
  }));

  const createMutation = useMutation({
    mutationFn: (body: CreateValues & { memberships: MembershipAssignment[] }) => api.createUser(body),
    onSuccess: (createdUser, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
      setCreateOpen(false);
      createDraft.reset();
      setCreateFeedback(
        `The account for ${createdUser.email} was created with access to ${variables.memberships.length} organization(s).`,
      );
      toast({ title: "User created", variant: "success" });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "The account could not be created.";
      toast({
        title: "Create failed",
        description: message,
        variant: "destructive",
      });
    },
  });

  const membershipMutation = useMutation({
    mutationFn: ({ userId, memberships }: { userId: number; memberships: MembershipAssignment[] }) =>
      api.updateUserMemberships(userId, { memberships }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
      setManaging(null);
      membershipDraft.reset();
      toast({ title: "User access updated", variant: "success" });
    },
    onError: (error: unknown) => toast({
      title: "Access update failed",
      description: error instanceof Error ? error.message : "Unknown",
      variant: "destructive",
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: number) => api.deleteUser(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
      setDeleting(null);
      toast({ title: "User deleted", variant: "success" });
    },
    onError: (error: unknown) =>
      toast({
        title: "Delete failed",
        description: error instanceof Error ? error.message : "Unknown",
        variant: "destructive",
      }),
  });

  return (
    <>
      <PageHeader
        title="Users"
        description={
          isPlatformAdmin
            ? "Platform admins manage organization memberships from here."
            : "Organization admins manage memberships for their active organization."
        }
        actions={
          <Dialog
            open={createOpen}
            onOpenChange={(open) => {
              if (open) {
                createDraft.reset();
                setCreateOpen(true);
                createMutation.reset();
                setCreateFeedback(null);
              } else if (createDraft.canClose(createMutation.isPending)) setCreateOpen(false);
            }}
          >
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4" /> Add user
              </Button>
            </DialogTrigger>
            <DialogContent onChangeCapture={createDraft.onChangeCapture} onClickCapture={createDraft.onClickCapture} onKeyDownCapture={createDraft.onKeyDownCapture}>
              <CreateUserForm
                tenants={tenantOptions}
                roles={roleOptions}
                canChooseTenant={isPlatformAdmin}
                defaultTenantId={me?.tenant_id ?? null}
                submitting={createMutation.isPending}
                submitError={
                  createMutation.error instanceof Error ? createMutation.error.message : null
                }
                onCancel={() => { if (createDraft.canClose(createMutation.isPending)) setCreateOpen(false); }}
                onSubmit={(values) => createMutation.mutate(values)}
              />
            </DialogContent>
          </Dialog>
        }
      />

      {createFeedback ? (
        <div
          role="status"
          className="mb-4 rounded-md border border-primary/40 bg-primary/10 px-4 py-3 text-sm"
        >
          {createFeedback}
        </div>
      ) : null}

      {usersQuery.error ? (
        <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {usersQuery.error instanceof Error ? usersQuery.error.message : "Failed to load"}
        </div>
      ) : null}

      <AdminListLayout filters={
        <AdminFilterPanel count={[organizationFilter, roleFilter, list.search.trim()].filter(Boolean).length} onClear={() => {
          setOrganizationFilter(""); setRoleFilter(""); list.setSearch("");
        }}>
          <AdminFilterGroup label="Access">
            <ListFilter label="Organization filter" value={organizationFilter} options={organizationOptions} onChange={setOrganizationFilter} />
            <ListFilter label="Role filter" value={roleFilter} options={ALL_ROLES.map((role) => ({
              value: role, label: role.replaceAll("_", " "),
            }))} onChange={setRoleFilter} />
          </AdminFilterGroup>
          <AdminFilterGroup label="Search">
        <ListSearch
          label="Search users"
          placeholder="Search names, emails, organizations, roles..."
          value={list.search}
          onChange={list.setSearch}
        />
        <ListSort {...list} />
          </AdminFilterGroup>
        </AdminFilterPanel>
      }>

      <Card className="min-w-0 max-w-full">
        <CardContent className="min-w-0 overflow-x-auto p-0">
          <table className="w-full min-w-[820px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Access</th>
                <th className="px-4 py-3 text-right">ID</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {usersQuery.isLoading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={idx} className="border-b border-border">
                    <td className="px-4 py-3" colSpan={5}>
                      <Skeleton className="h-5 w-full" />
                    </td>
                  </tr>
                ))
              ) : usersQuery.error && !usersQuery.data ? (
                <tr><td className="px-4 py-8 text-center" colSpan={4}>
                  Unable to load users.{" "}
                  <Button variant="outline" size="sm" onClick={() => void usersQuery.refetch()}>Retry</Button>
                </td></tr>
              ) : list.total > 0 ? (
                list.rows.map((user) => (
                  <tr
                    key={user.user_id}
                    className="border-b border-border last:border-0 hover:bg-accent/40"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarFallback>{initialsFromName(user.display_name)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-semibold text-foreground">{user.display_name}</div>
                          <div className="text-xs text-muted-foreground">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        {user.memberships.map((membership) => (
                          <div key={`${membership.tenant_id ?? "platform"}-${membership.role}`} className="flex items-center gap-1.5">
                            <span className="text-xs text-muted-foreground">
                              {membership.name ?? "Platform"}
                            </span>
                            <RolePill role={membership.role} />
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="tabular px-4 py-3 text-right text-xs text-muted-foreground">
                      {user.user_id}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => { membershipDraft.reset(); setManaging(user); }}>
                          Manage access
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => setDeleting(user)}
                        >
                          <Trash2 className="h-4 w-4" /> Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="px-4 py-8 text-center text-muted-foreground" colSpan={5}>
                    {[organizationFilter, roleFilter, list.search.trim()].some(Boolean) ? "No users match the current filter." : "No users found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
        <ListPagination {...list} />
      </Card>
      </AdminListLayout>

      <ManageMembershipsDialog
        user={managing}
        tenants={tenantOptions}
        roles={roleOptions}
        canChooseTenant={isPlatformAdmin}
        submitting={membershipMutation.isPending}
        submitError={membershipMutation.error instanceof Error ? membershipMutation.error.message : null}
        onOpenChange={(open) => { if (!open && membershipDraft.canClose(membershipMutation.isPending)) setManaging(null); }}
        onChangeCapture={membershipDraft.onChangeCapture}
        onClickCapture={membershipDraft.onClickCapture}
        onKeyDownCapture={membershipDraft.onKeyDownCapture}
        onSubmit={(memberships) => managing && membershipMutation.mutate({ userId: managing.user_id, memberships })}
      />

      <Dialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete user?</DialogTitle>
            <DialogDescription>
              Permanently removes membership for{" "}
              <span className="font-semibold text-foreground">{deleting?.display_name}</span>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleting && deleteMutation.mutate(deleting.user_id)}
            >
              {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Delete user
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

type CreateUserFormProps = {
  tenants: Tenant[];
  roles: Role[];
  canChooseTenant: boolean;
  defaultTenantId: number | null;
  submitting: boolean;
  submitError: string | null;
  onCancel: () => void;
  onSubmit: (values: CreateValues & { memberships: MembershipAssignment[] }) => void;
};

function CreateUserForm({
  tenants,
  roles,
  canChooseTenant,
  defaultTenantId,
  submitting,
  submitError,
  onCancel,
  onSubmit,
}: CreateUserFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateValues>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      email: "",
      display_name: "",
      password: "",
    },
  });
  const [memberships, setMemberships] = useState<MembershipDraft[]>([
    {
      local_id: 1,
      role: defaultTenantId === null ? "platform_admin" : "tenant_viewer",
      tenant_id: defaultTenantId,
    },
  ]);
  const [membershipError, setMembershipError] = useState<string | null>(null);

  return (
    <form
      onSubmit={handleSubmit((values) => {
        const normalized = validateMembershipDrafts(memberships, canChooseTenant, defaultTenantId);
        if (typeof normalized === "string") {
          setMembershipError(normalized);
          return;
        }
        setMembershipError(null);
        onSubmit({ ...values, memberships: normalized });
      })}
      className="space-y-4"
      noValidate
    >
      <DialogHeader>
        <DialogTitle>Add user</DialogTitle>
        <DialogDescription>
          Create a sign-in account with an initial password and add it to a tenant or the platform.
        </DialogDescription>
      </DialogHeader>
      {submitError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {submitError}
        </p>
      ) : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" {...register("email")} />
          {errors.email ? <p className="text-xs text-destructive">{errors.email.message}</p> : null}
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="display_name">Display name</Label>
          <Input id="display_name" {...register("display_name")} />
          {errors.display_name ? (
            <p className="text-xs text-destructive">{errors.display_name.message}</p>
          ) : null}
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="password">Initial password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            {...register("password")}
          />
          {errors.password ? (
            <p className="text-xs text-destructive">{errors.password.message}</p>
          ) : null}
        </div>
      </div>
      <MembershipAssignmentEditor
        memberships={memberships}
        setMemberships={setMemberships}
        tenants={tenants}
        roles={roles}
        canChooseTenant={canChooseTenant}
        defaultTenantId={defaultTenantId}
      />
      {membershipError ? <p role="alert" className="text-xs text-destructive">{membershipError}</p> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Create user
        </Button>
      </DialogFooter>
    </form>
  );
}

function MembershipAssignmentEditor({
  memberships,
  setMemberships,
  tenants,
  roles,
  canChooseTenant,
  defaultTenantId,
}: {
  memberships: MembershipDraft[];
  setMemberships: (memberships: MembershipDraft[]) => void;
  tenants: Tenant[];
  roles: Role[];
  canChooseTenant: boolean;
  defaultTenantId: number | null;
}): JSX.Element {
  const updateMembership = (localId: number, patch: Partial<MembershipDraft>): void => {
    setMemberships(memberships.map((membership) =>
      membership.local_id === localId ? { ...membership, ...patch } : membership,
    ));
  };

  return (
    <section className="space-y-3 rounded-md border border-border p-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Organization access</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            One sign-in can have a different role in each organization.
          </p>
        </div>
        {canChooseTenant ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-draft-change
            onClick={() => setMemberships([
              ...memberships,
              {
                local_id: Math.max(0, ...memberships.map((membership) => membership.local_id)) + 1,
                role: "tenant_viewer",
                tenant_id: defaultTenantId,
              },
            ])}
          >
            <Plus className="h-4 w-4" /> Add membership
          </Button>
        ) : null}
      </div>
      <div className="space-y-3">
        {memberships.map((membership, index) => {
          const availableRoles = membership.tenant_id === null
            ? roles.filter((role) => role === "platform_admin")
            : roles.filter((role) => role !== "platform_admin");
          return (
            <div key={membership.local_id} className="grid gap-3 rounded-md bg-muted/30 p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
              <div className="space-y-2">
                <Label htmlFor={`membership-organization-${membership.local_id}`}>
                  Organization for membership {index + 1}
                </Label>
                <SearchableSelector
                  label={`Organization for membership ${index + 1}`}
                  size="form"
                  id={`membership-organization-${membership.local_id}`}
                  placeholder="Select organization"
                  options={[
                    ...(canChooseTenant ? [{ value: "__platform__", label: "Platform" }] : []),
                    ...tenants.map((tenant) => ({
                      value: String(tenant.tenant_id),
                      label: `${tenant.name} · ${tenant.kind === "vendor" ? "Vendor" : "Distribution company"}`,
                    })),
                  ]}
                  value={membership.tenant_id === null ? "__platform__" : String(membership.tenant_id)}
                  disabled={!canChooseTenant}
                  onChange={(value) => {
                    const tenantId = value === "__platform__" ? null : Number(value);
                    const nextRole = tenantId === null
                      ? "platform_admin"
                      : membership.role === "platform_admin" ? "tenant_viewer" : membership.role;
                    updateMembership(membership.local_id, { tenant_id: tenantId, role: nextRole });
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor={`membership-role-${membership.local_id}`}>
                  Role for membership {index + 1}
                </Label>
                <Select
                  value={membership.role}
                  onValueChange={(role) => updateMembership(membership.local_id, { role: role as Role })}
                >
                  <SelectTrigger id={`membership-role-${membership.local_id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRoles.map((role) => (
                      <SelectItem key={role} value={role}>{role.replaceAll("_", " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {canChooseTenant ? (
                <div className="flex items-end justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove membership ${index + 1}`}
                    disabled={memberships.length === 1}
                    data-draft-change
                    onClick={() => setMemberships(memberships.filter((item) => item.local_id !== membership.local_id))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function validateMembershipDrafts(
  memberships: MembershipDraft[],
  canChooseTenant: boolean,
  currentTenantId: number | null,
): MembershipAssignment[] | string {
  if (memberships.length === 0) return "Add at least one organization membership.";
  const tenantIds = memberships.map((membership) => membership.tenant_id);
  if (new Set(tenantIds).size !== tenantIds.length) {
    return "Choose each organization only once. Assign one role per organization.";
  }
  if (!canChooseTenant && memberships.some((membership) => membership.tenant_id !== currentTenantId)) {
    return "You can only manage access in your active organization.";
  }
  if (memberships.some((membership) =>
    (membership.tenant_id === null) !== (membership.role === "platform_admin")
  )) {
    return "Platform admins need Platform access. Organization members need an organization role.";
  }
  return memberships.map(({ tenant_id, role }) => ({ tenant_id, role }));
}

function ManageMembershipsDialog({
  user,
  tenants,
  roles,
  canChooseTenant,
  submitting,
  submitError,
  onOpenChange,
  onSubmit,
  onChangeCapture,
  onClickCapture,
  onKeyDownCapture,
}: {
  user: UserSummary | null;
  tenants: Tenant[];
  roles: Role[];
  canChooseTenant: boolean;
  submitting: boolean;
  submitError: string | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (memberships: MembershipAssignment[]) => void;
  onChangeCapture: FormEventHandler<HTMLElement>;
  onClickCapture: MouseEventHandler<HTMLElement>;
  onKeyDownCapture: KeyboardEventHandler<HTMLElement>;
}): JSX.Element {
  return (
    <Dialog open={user !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" onChangeCapture={onChangeCapture} onClickCapture={onClickCapture} onKeyDownCapture={onKeyDownCapture}>
        {user ? (
          <ManageMembershipsForm
            key={user.user_id}
            user={user}
            tenants={tenants}
            roles={roles}
            canChooseTenant={canChooseTenant}
            submitting={submitting}
            submitError={submitError}
            onCancel={() => onOpenChange(false)}
            onSubmit={onSubmit}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function ManageMembershipsForm({
  user,
  tenants,
  roles,
  canChooseTenant,
  submitting,
  submitError,
  onCancel,
  onSubmit,
}: {
  user: UserSummary;
  tenants: Tenant[];
  roles: Role[];
  canChooseTenant: boolean;
  submitting: boolean;
  submitError: string | null;
  onCancel: () => void;
  onSubmit: (memberships: MembershipAssignment[]) => void;
}): JSX.Element {
  const [memberships, setMemberships] = useState<MembershipDraft[]>(user.memberships.map((membership, index) => ({
    local_id: index + 1,
    tenant_id: membership.tenant_id,
    role: membership.role,
  })));
  const [membershipError, setMembershipError] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const defaultTenantId = user.memberships.find((membership) => membership.tenant_id !== null)?.tenant_id ?? null;
  return (
    <>
      <DialogHeader>
        <DialogTitle>Manage access for {user.display_name}</DialogTitle>
        <DialogDescription>
          Keep one sign-in account. Choose one role for each organization.
        </DialogDescription>
      </DialogHeader>
      {submitError ? <p role="alert" className="text-sm text-destructive">{submitError}</p> : null}
      <MembershipAssignmentEditor
        memberships={memberships}
        setMemberships={(next) => { setReviewing(false); setMemberships(next); }}
        tenants={tenants}
        roles={roles}
        canChooseTenant={canChooseTenant}
        defaultTenantId={defaultTenantId}
      />
      {membershipError ? <p role="alert" className="text-xs text-destructive">{membershipError}</p> : null}
      {reviewing ? <section aria-label="Review access changes" className="rounded-md border p-3 text-sm">
        <h3 className="font-semibold">Review access changes</h3>
        <p>Current: {user.memberships.map((membership) => `${membership.name ?? "Platform"} (${membership.role})`).join(", ") || "None"}</p>
        <p>New: {memberships.map((membership) => `${tenants.find((tenant) => tenant.tenant_id === membership.tenant_id)?.name ?? (membership.tenant_id === null ? "Platform" : String(membership.tenant_id))} (${membership.role})`).join(", ") || "None"}</p>
      </section> : null}
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button
          disabled={submitting}
          onClick={() => {
            const normalized = validateMembershipDrafts(memberships, canChooseTenant, defaultTenantId);
            if (typeof normalized === "string") {
              setMembershipError(normalized);
              return;
            }
            if (!reviewing) { setMembershipError(null); setReviewing(true); }
            else onSubmit(normalized);
          }}
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {reviewing ? "Save access" : "Review access"}
        </Button>
      </DialogFooter>
    </>
  );
}
